import { Placeholder } from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Redo2, Sparkles, Undo2, Underline } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'react-toastify';

import { FieldLabel, Textarea as KitTextarea } from '~/components/form';
import type { PlacementPreviewFieldProps } from '~/components/pages/ai/placements/preview/TextField';
import { Button, Modal, ModalActions, Spinner } from '~/components/ui';
import { formatPromptAssistErrorMessage, promptAssistErrorStatus } from '~/lib/ai/promptAssist';

const EMPTY_HTML = '<p></p>';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Rich text may arrive as HTML or as already-rendered plain text; both become valid editor content. */
function toEditorHtml(value: string): string {
  if (!value.trim()) {
    return EMPTY_HTML;
  }

  return value.includes('<') && value.includes('>') ? value : `<p>${escapeHtml(value)}</p>`;
}

const stripHtml = (value: string): string =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

interface ToolbarButtonProps {
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: ReactNode;
  onClick: () => void;
}

function ToolbarButton({
  active = false,
  disabled = false,
  label,
  children,
  onClick,
}: ToolbarButtonProps) {
  return (
    <Button
      variant="ghost"
      size="icon"
      ariaLabel={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={active ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white' : undefined}
      testId={`placement-preview-rich-text-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {children}
    </Button>
  );
}

/**
 * The Rich Text placement preview: TipTap with the toolbar and the AI Write dialog its
 * source component had. The AI menu here is a single writer prompt, not the Edit & Refine
 * menu the two plain-text previews use.
 */
export default function RichText({
  aiDisableTooltip,
  aiDisabled,
  defaultValue = '',
  disabledTooltip,
  isDisabled,
  isRunning,
  onRunAi,
  placeholder,
}: PlacementPreviewFieldProps) {
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const editor = useEditor({
    content: toEditorHtml(defaultValue),
    editable: !isDisabled,
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: placeholder ?? 'Write your rich text content…' }),
    ],
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-label': 'Rich text editor',
        'aria-multiline': 'true',
      },
    },
  });

  useEffect(() => {
    editor?.commands.setContent(toEditorHtml(defaultValue));
  }, [defaultValue, editor]);

  useEffect(() => {
    editor?.setEditable(!isDisabled);
  }, [editor, isDisabled]);

  const openAi = () => {
    setInstructions('');
    setIsAiOpen(true);
  };

  const generate = async () => {
    const nextInstructions = instructions.trim();

    if (!nextInstructions) {
      return;
    }

    if (!onRunAi) {
      toast.error('AI actions are unavailable until the placement is saved.');
      return;
    }

    setIsGenerating(true);

    try {
      const response = await onRunAi({
        action: 'custom-prompt',
        inputText: stripHtml(editor?.getHTML() ?? defaultValue),
        outputFormat: 'html',
        userInstruction: nextInstructions,
      });

      const output = response?.outputText.trim();

      if (!output) {
        throw new Error('The AI returned an empty response.');
      }

      editor?.commands.setContent(toEditorHtml(output));
      setIsAiOpen(false);
      setInstructions('');
    } catch (error) {
      toast.error(
        formatPromptAssistErrorMessage({
          message: error instanceof Error ? error.message : 'The AI request failed.',
          status: promptAssistErrorStatus(error),
        }),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const disabledReason = isDisabled ? disabledTooltip : aiDisabled ? aiDisableTooltip : undefined;

  return (
    <div title={disabledReason}>
      <FieldLabel
        labelAction={
          isDisabled || aiDisabled ? (
            <Button
              variant="ghost"
              size="icon"
              disabled
              ariaLabel="AI writer unavailable"
              title={disabledReason ?? 'AI writer is unavailable.'}
              startIcon={<Sparkles className="size-4" aria-hidden="true" />}
            />
          ) : (
            <Button
              variant="ghost"
              size="icon"
              ariaLabel="AI writer"
              title="AI writer"
              disabled={isRunning}
              onClick={openAi}
              startIcon={<Sparkles className="size-4" aria-hidden="true" />}
              testId="placement-preview-rich-text-ai"
            />
          )
        }
      >
        Rich text
      </FieldLabel>

      <div
        className={`overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700 ${isDisabled ? 'pointer-events-none opacity-60' : ''}`}
      >
        <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1 dark:border-gray-800 dark:bg-white/[0.03]">
          <ToolbarButton
            label="Bold"
            active={editor?.isActive('bold')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <Bold className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            active={editor?.isActive('italic')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <Italic className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label="Underline"
            active={editor?.isActive('underline')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
          >
            <Underline className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label="Heading"
            active={editor?.isActive('heading', { level: 2 })}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <span className="text-sm font-semibold">H</span>
          </ToolbarButton>
          <ToolbarButton
            label="Bullet list"
            active={editor?.isActive('bulletList')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          >
            <List className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label="Ordered list"
            active={editor?.isActive('orderedList')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label="Undo"
            disabled={!editor?.can().undo()}
            onClick={() => editor?.chain().focus().undo().run()}
          >
            <Undo2 className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label="Redo"
            disabled={!editor?.can().redo()}
            onClick={() => editor?.chain().focus().redo().run()}
          >
            <Redo2 className="size-4" aria-hidden="true" />
          </ToolbarButton>
        </div>
        <EditorContent
          editor={editor}
          className="min-h-44 px-3 py-2 text-sm text-gray-800 dark:text-white/90 [&_.ProseMirror]:outline-none [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5"
        />
      </div>

      {isDisabled && disabledTooltip ? (
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{disabledTooltip}</p>
      ) : null}

      <Modal
        isOpen={isAiOpen}
        onClose={() => setIsAiOpen(false)}
        size="large"
        title="AI writer"
        description="Describe the content you want. The placement's prompt and sample data are used for this draft."
        testId="placement-preview-rich-text-dialog"
        busy={isGenerating}
        footer={
          <ModalActions
            onDismiss={() => setIsAiOpen(false)}
            dismissLabel="Cancel"
            dismissDisabled={isGenerating}
          >
            <Button
              variant="primary"
              onClick={generate}
              loading={isGenerating}
              disabled={!instructions.trim()}
              testId="placement-preview-rich-text-generate"
            >
              Generate
            </Button>
          </ModalActions>
        }
      >
        <div className="space-y-4">
          {isGenerating ? (
            <p
              role="status"
              className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
            >
              <Spinner size="sm" />
              Generating…
            </p>
          ) : null}
          <KitTextarea
            id="placement-preview-rich-text-instructions"
            label="Instructions"
            testId="placement-preview-rich-text-instructions"
            value={instructions}
            onChange={setInstructions}
            rows={6}
            placeholder="A short marketing section with bullet points and a call to action."
            helperText="Applied to the content currently in the editor."
          />
        </div>
      </Modal>
    </div>
  );
}
